/**
 * NR Event — Add to calendar (Google Calendar + Apple .ics)
 * Expects: [data-nr-calendar] root with data-title, data-date, data-time, data-location, data-description
 * Toggle reveals Google / Apple choices; click outside or Escape collapses.
 */
(function () {
  'use strict';

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function parseHourMinute(token) {
    if (!token) return null;
    var cleaned = String(token).trim().toLowerCase().replace(/\s+/g, '');
    var match = cleaned.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);
    if (!match) return null;
    var hour = parseInt(match[1], 10);
    var minute = match[2] ? parseInt(match[2], 10) : 0;
    var meridiem = match[3];
    if (meridiem === 'pm' && hour < 12) hour += 12;
    if (meridiem === 'am' && hour === 12) hour = 0;
    if (hour > 23 || minute > 59) return null;
    return { hour: hour, minute: minute };
  }

  function parseTimeRange(timeText) {
    if (!timeText) return { start: null, end: null };
    var parts = String(timeText)
      .toLowerCase()
      .split(/\s*[-–—to]+\s*/i)
      .map(function (p) {
        return p.trim();
      })
      .filter(Boolean);

    var start = parseHourMinute(parts[0]);
    var end = parts.length > 1 ? parseHourMinute(parts[1]) : null;

    // If end has no meridiem but start does, inherit (e.g. 3-10pm)
    if (start && end && parts[1] && !/(am|pm)/i.test(parts[1]) && /(am|pm)/i.test(parts[0])) {
      var startMer = parts[0].match(/(am|pm)/i);
      if (startMer) {
        end = parseHourMinute(parts[1] + startMer[1]);
      }
    }

    // "3pm-10pm" style where only end has meridiem
    if (!start && parts[0] && parts.length > 1 && /(am|pm)/i.test(parts[1])) {
      var endMer = parts[1].match(/(am|pm)/i);
      start = parseHourMinute(parts[0] + (endMer ? endMer[1] : ''));
      end = parseHourMinute(parts[1]);
    }

    return { start: start, end: end };
  }

  function buildLocalDate(dateStr, hm, fallbackHour) {
    var parts = String(dateStr).split('-');
    if (parts.length !== 3) return null;
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10) - 1;
    var d = parseInt(parts[2], 10);
    if (!y || m < 0 || m > 11 || !d) return null;
    var hour = hm ? hm.hour : fallbackHour;
    var minute = hm ? hm.minute : 0;
    var dt = new Date(y, m, d, hour, minute, 0);
    return isNaN(dt.getTime()) ? null : dt;
  }

  /** Google + ICS expect UTC stamps ending in Z */
  function toUtcStamp(date) {
    return (
      date.getUTCFullYear() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      'T' +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) +
      'Z'
    );
  }

  function toAllDayStamp(dateStr) {
    return String(dateStr).replace(/-/g, '');
  }

  function nextDayStamp(dateStr) {
    var parts = String(dateStr).split('-');
    var dt = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    dt.setDate(dt.getDate() + 1);
    return dt.getFullYear() + pad(dt.getMonth() + 1) + pad(dt.getDate());
  }

  function icsEscape(text) {
    return String(text || '')
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');
  }

  function prefersOpenIcs() {
    var ua = navigator.userAgent || '';
    // iPhone / iPod
    if (/iPhone|iPod/.test(ua)) return true;
    // iPad (old UA or iPadOS desktop UA)
    if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return true;
    }
    // Safari on macOS — hands .ics off to Calendar.app
    if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|Edg|Firefox/.test(ua)) {
      return true;
    }
    return false;
  }

  /**
   * On iOS / Safari, open the .ics so Calendar/iCloud can import it.
   * Elsewhere, trigger a file download (user opens the file).
   */
  function openOrDownloadIcs(filename, content) {
    var blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);

    if (prefersOpenIcs()) {
      var opened = window.open(url, '_blank');
      if (!opened) {
        window.location.href = url;
      }
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 10000);
      return;
    }

    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(function () {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  function buildEvents(root) {
    var title = root.getAttribute('data-title') || 'Event';
    var dateStr = (root.getAttribute('data-date') || '').trim();
    var timeText = root.getAttribute('data-time') || '';
    var location = root.getAttribute('data-location') || '';
    var description = root.getAttribute('data-description') || '';
    var range = parseTimeRange(timeText);
    var start = buildLocalDate(dateStr, range.start, 15);
    var end = buildLocalDate(dateStr, range.end, range.start ? range.start.hour + 3 : 18);

    // Overnight range (e.g. 10pm - 4am): the end falls on the next day
    if (start && end && end <= start) {
      end.setDate(end.getDate() + 1);
    }

    return {
      title: title,
      dateStr: dateStr,
      location: location,
      description: description,
      start: start,
      end: end,
      allDay: !range.start
    };
  }

  function googleUrl(ev) {
    var params = new URLSearchParams();
    params.set('action', 'TEMPLATE');
    params.set('text', ev.title);
    if (ev.location) params.set('location', ev.location);
    if (ev.description) params.set('details', ev.description);

    if (ev.allDay || !ev.start) {
      params.set('dates', toAllDayStamp(ev.dateStr) + '/' + nextDayStamp(ev.dateStr));
    } else {
      params.set('dates', toUtcStamp(ev.start) + '/' + toUtcStamp(ev.end || ev.start));
    }

    // Google Calendar prefers %20 over + for spaces
    return (
      'https://calendar.google.com/calendar/render?' +
      params.toString().replace(/\+/g, '%20')
    );
  }

  function icsContent(ev) {
    var uid = 'nr-' + ev.dateStr + '-' + Date.now() + '@nothingradio.com';
    var now = new Date();
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Nothing Radio//Event//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + uid,
      'DTSTAMP:' + toUtcStamp(now),
      'SUMMARY:' + icsEscape(ev.title)
    ];

    if (ev.allDay || !ev.start) {
      lines.push('DTSTART;VALUE=DATE:' + toAllDayStamp(ev.dateStr));
      lines.push('DTEND;VALUE=DATE:' + nextDayStamp(ev.dateStr));
    } else {
      lines.push('DTSTART:' + toUtcStamp(ev.start));
      lines.push('DTEND:' + toUtcStamp(ev.end || ev.start));
    }

    if (ev.location) lines.push('LOCATION:' + icsEscape(ev.location));
    if (ev.description) lines.push('DESCRIPTION:' + icsEscape(ev.description));
    lines.push('END:VEVENT', 'END:VCALENDAR');
    return lines.join('\r\n');
  }

  function eventSlug(ev) {
    return (
      String(ev.title || 'event')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 48) || 'event'
    );
  }

  function setOpen(root, open) {
    var toggle = root.querySelector('[data-nr-calendar-toggle]');
    var choices = root.querySelector('[data-nr-calendar-choices]');
    if (!toggle || !choices) return;

    root.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.hidden = open;
    choices.hidden = !open;
  }

  function closeAll(except) {
    document.querySelectorAll('[data-nr-calendar].is-open').forEach(function (root) {
      if (root !== except) setOpen(root, false);
    });
  }

  function bindRoot(root) {
    var toggle = root.querySelector('[data-nr-calendar-toggle]');
    var googleBtn = root.querySelector('[data-nr-calendar-google]');
    var appleBtn = root.querySelector('[data-nr-calendar-apple]');
    if (!toggle) return;

    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      var willOpen = !root.classList.contains('is-open');
      closeAll(willOpen ? root : null);
      setOpen(root, willOpen);
    });

    if (googleBtn) {
      googleBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var ev = buildEvents(root);
        if (!ev.dateStr) return;
        if (!ev.allDay && !ev.start) return;

        var url = googleUrl(ev);
        // Same-tab fallback if popup blocked — keeps the template URL intact
        var win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) window.location.assign(url);
        setOpen(root, false);
      });
    }

    if (appleBtn) {
      appleBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var ev = buildEvents(root);
        if (!ev.dateStr) return;
        if (!ev.allDay && !ev.start) return;
        openOrDownloadIcs(eventSlug(ev) + '.ics', icsContent(ev));
        setOpen(root, false);
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-nr-calendar]').forEach(bindRoot);

    document.addEventListener('click', function (e) {
      var openRoots = document.querySelectorAll('[data-nr-calendar].is-open');
      if (!openRoots.length) return;
      openRoots.forEach(function (root) {
        if (!root.contains(e.target)) setOpen(root, false);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
